/**
 * Tiny typed wrapper around Google Identity Services (SPEC_V2 §1).
 *
 * We use `google.accounts.id.initialize` and `renderButton`, plus One Tap
 * (`prompt()`) on the landing page when the script offers it; the e2e
 * suite's minimal fake has no prompt(), so One Tap is optional by design.
 */

export const GIS_SRC = 'https://accounts.google.com/gsi/client';

export interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

export interface GoogleIdConfig {
  client_id: string;
  callback: (r: GoogleCredentialResponse) => void;
  ux_mode: 'popup';
  context?: 'signin' | 'signup' | 'use';
  cancel_on_tap_outside?: boolean;
  itp_support?: boolean;
  use_fedcm_for_prompt?: boolean;
}

export interface GoogleButtonOptions {
  theme: 'filled_black' | 'outline' | 'filled_blue';
  size: 'large' | 'medium' | 'small';
  shape: 'pill' | 'rectangular' | 'circle' | 'square';
  text: 'continue_with' | 'signin_with' | 'signup_with' | 'signin';
}

export interface GoogleIdApi {
  initialize(config: GoogleIdConfig): void;
  renderButton(parent: HTMLElement, options: GoogleButtonOptions): void;
  prompt?: () => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

export const BUTTON_OPTIONS: GoogleButtonOptions = { theme: 'filled_black', size: 'large', shape: 'pill', text: 'continue_with' };

let loading: Promise<GoogleIdApi> | null = null;

function current(): GoogleIdApi | null {
  const id = typeof window !== 'undefined' ? window.google?.accounts?.id : undefined;
  return id && typeof id.initialize === 'function' && typeof id.renderButton === 'function' ? id : null;
}

/** Load the GIS script once (dynamically) and resolve with `google.accounts.id`. */
export function loadGoogleIdentity(timeoutMs = 15_000): Promise<GoogleIdApi> {
  const ready = current();
  if (ready) return Promise.resolve(ready);
  if (loading) return loading;
  loading = new Promise<GoogleIdApi>((resolve, reject) => {
    let script = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const done = (err?: Error) => {
      clearTimeout(timer);
      const api = current();
      if (api && !err) resolve(api);
      else {
        loading = null;
        script?.remove();
        reject(err ?? new Error('Google sign-in didn’t load.'));
      }
    };
    const timer = setTimeout(() => done(new Error('Google sign-in took too long to load.')), timeoutMs);
    if (!script) {
      script = document.createElement('script');
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => done(), { once: true });
    script.addEventListener('error', () => done(new Error('Google sign-in couldn’t load. Check your connection or content blockers.')), { once: true });
  });
  return loading;
}

/**
 * Initialise GIS for `clientId` and render the official button into `el`.
 * `onCredential` receives the ID token to POST to /v1/auth/google.
 */
export function renderGoogleButton(
  api: GoogleIdApi,
  el: HTMLElement,
  clientId: string,
  onCredential: (credential: string) => void,
  opts: { oneTap?: boolean; text?: GoogleButtonOptions['text']; theme?: GoogleButtonOptions['theme'] } = {},
): void {
  api.initialize({
    client_id: clientId,
    callback: (r) => {
      if (r?.credential) onCredential(r.credential);
    },
    ux_mode: 'popup',
    context: 'signup',
    cancel_on_tap_outside: true,
    itp_support: true,
    use_fedcm_for_prompt: true,
  });
  el.replaceChildren();
  api.renderButton(el, { ...BUTTON_OPTIONS, ...(opts.text ? { text: opts.text } : {}), ...(opts.theme ? { theme: opts.theme } : {}) });
  // One Tap: Chrome and Android show "Continue as …" for the Google account
  // already on the device, the fastest way in. It never shows again after a
  // dismissal for a while (Google's own cool-down), so it's never nagging.
  if (opts.oneTap && typeof api.prompt === 'function') {
    try {
      api.prompt();
    } catch {
      /* One Tap is a bonus; the button is always there */
    }
  }
}

/** Test hook: forget the cached loader. */
export function __resetGoogleLoader(): void {
  loading = null;
}
